// CoreUI Pro source reference: /Users/guochunzhong/git/oss/coreui-pro/js/src/range-slider.js
import BaseComponent from "./base_component"
import { parseList } from "./dom"

export default class RangeSlider extends BaseComponent {
  static componentName = "range-slider"
  static defaults = {
    clickableLabels: true,
    disabled: false,
    distance: 0,
    labels: false,
    max: 100,
    min: 0,
    step: 1,
    tooltips: true,
    track: "fill",
    value: 0,
    vertical: false,
  }

  constructor(element, config = {}) {
    super(element, config)
    this.values = parseList(this.config.value).map(Number)
    if (!this.values.length) this.values = [Number(this.config.value) || 0]
    this.render()
    this.bindEvents()
  }

  render() {
    this.element.replaceChildren()
    this.element.className = this.originalClassName
    this.element.classList.add("range-slider")
    if (this.config.vertical) this.element.classList.add("range-slider-vertical")
    if (this.config.disabled) this.element.classList.add("disabled")
    const container = document.createElement("div")
    container.className = "range-slider-inputs-container"
    this.inputs = this.values.map((value, index) => {
      const input = document.createElement("input")
      input.className = "range-slider-input"
      input.type = "range"
      const name = Array.isArray(this.config.name)
        ? this.config.name[index]
        : this.config.name && `${this.config.name}-${index}`
      if (name !== undefined && name !== null && name !== "" && name !== false) input.name = String(name)
      input.min = this.config.min
      input.max = this.config.max
      input.step = this.config.step
      input.value = value
      input.disabled = Boolean(this.config.disabled)
      input.setAttribute("aria-valuemin", this.config.min)
      input.setAttribute("aria-valuemax", this.config.max)
      input.setAttribute("aria-valuenow", value)
      input.setAttribute("aria-orientation", this.config.vertical ? "vertical" : "horizontal")
      input.setAttribute(
        "aria-label",
        this.config.ariaLabels?.[index] ||
          (this.values.length === 2 ? (index === 0 ? "Minimum value" : "Maximum value") : `Value ${index + 1}`),
      )
      container.append(input)
      if (this.config.tooltips) {
        const tooltip = document.createElement("div")
        tooltip.className = "range-slider-tooltip"
        tooltip.innerHTML = `<div class="range-slider-tooltip-inner">${value}</div><div class="range-slider-tooltip-arrow"></div>`
        container.append(tooltip)
      }
      return input
    })
    this.track = document.createElement("div")
    this.track.className = "range-slider-track"
    container.append(this.track)
    this.element.append(container)
    this.renderLabels()
    this.updateVisuals()
  }

  renderLabels() {
    const labels = parseList(this.config.labels)
    if (!labels.length) return
    const container = document.createElement("div")
    container.className = "range-slider-labels-container"
    labels.forEach((label, index) => {
      const element = document.createElement("div")
      element.className = `range-slider-label${this.config.clickableLabels && !this.config.disabled ? " clickable" : ""}`
      element.textContent = label
      const percent = labels.length === 1 ? 0 : (index / (labels.length - 1)) * 100
      element.dataset.coreuiValue =
        Number(this.config.min) + (percent / 100) * (Number(this.config.max) - Number(this.config.min))
      element.style[this.config.vertical ? "bottom" : "insetInlineStart"] = `${percent}%`
      container.append(element)
    })
    this.element.append(container)
  }

  bindEvents() {
    this.inputs.forEach((input, index) =>
      this.listen(input, "input", () => this.updateValue(index, Number(input.value))),
    )
    this.inputs.forEach((input) => this.listen(input, "change", () => this.emit("change", { value: [...this.values] })))
    this.listen(this.element, "click", (event) => {
      const label = event.target.closest(".range-slider-label.clickable")
      if (label) {
        const value = Number(label.dataset.coreuiValue)
        const nearest = this.values.reduce(
          (best, current, index) => (Math.abs(current - value) < Math.abs(this.values[best] - value) ? index : best),
          0,
        )
        this.updateValue(nearest, value)
        this.emit("change", { value: [...this.values] })
      }
    })
  }

  updateValue(index, value) {
    const distance = Number(this.config.distance || 0)
    const previous = index > 0 ? this.values[index - 1] + distance : Number(this.config.min)
    const next = index < this.values.length - 1 ? this.values[index + 1] - distance : Number(this.config.max)
    value = Math.max(previous, Math.min(next, value))
    this.values[index] = value
    this.inputs[index].value = value
    this.inputs[index].setAttribute("aria-valuenow", value)
    this.updateVisuals()
    this.emit("input", { value: [...this.values] })
  }

  updateVisuals() {
    const min = Number(this.config.min)
    const span = Number(this.config.max) - min
    const positions = this.values.map((value) => ((value - min) / span) * 100)
    this.inputs.forEach((input, index) => input.setAttribute("aria-valuetext", this.formatValue(this.values[index])))
    const from = Math.min(...positions)
    const to = Math.max(...positions)
    const direction = this.config.vertical ? "to top" : "to right"
    this.track.style.backgroundImage =
      this.values.length === 1
        ? `linear-gradient(${direction}, var(--cui-range-slider-track-in-range-bg) 0%, var(--cui-range-slider-track-in-range-bg) ${to}%, transparent ${to}%)`
        : `linear-gradient(${direction}, transparent 0%, transparent ${from}%, var(--cui-range-slider-track-in-range-bg) ${from}%, var(--cui-range-slider-track-in-range-bg) ${to}%, transparent ${to}%)`
    this.element.querySelectorAll(".range-slider-tooltip").forEach((tooltip, index) => {
      tooltip.querySelector(".range-slider-tooltip-inner").textContent = this.formatValue(this.values[index])
      tooltip.style[this.config.vertical ? "bottom" : "insetInlineStart"] = `${positions[index]}%`
    })
  }

  formatValue(value) {
    return this.config.tooltipsFormat ? this.config.tooltipsFormat(value) : String(value)
  }

  dispose() {
    if (this.signal.aborted) return
    this.element.innerHTML = this.originalHTML
    this.element.className = this.originalClassName
    super.dispose()
  }
  snapshotState() {
    return { config: { value: this.values } }
  }
}
